import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MapPinIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { careerTimeline } from '../data/profile';
import { Reveal } from './Reveal';
import { SectionLabel } from './SectionLabel';
export function CareerTimeline({
  showLabel = true
}: {
  showLabel?: boolean;
}) {
  const [openId, setOpenId] = useState<string>(careerTimeline[0].id);
  const reduceMotion = useReducedMotion();
  return <section id="experience" className="w-full bg-white py-20 lg:py-28" data-id="element-112" data-mp="3jgh1giqt74qy">
      <div className="mx-auto max-w-shell px-6 sm:px-8" data-id="element-113" data-mp="3jenu118dgghw">
        {showLabel && <Reveal data-id="element-114" data-mp="3o88r8r2556g1">
            <SectionLabel data-id="element-115" data-mp="3o9tcfq2kfd18">Career Timeline</SectionLabel>
            <h2 className="mt-6 max-w-2xl font-serif text-[32px] font-semibold leading-[1.12] tracking-[-0.015em] text-navy sm:text-4xl lg:text-[44px]" data-id="element-116" data-mp="3t58nka96uuty">
              Progression across leading global enterprises
            </h2>
          </Reveal>}

        <div className={showLabel ? 'mt-14' : ''} data-id="element-117" data-mp="1x2f0wsum00qj">
          <ul className="relative" data-id="element-118" data-mp="1x49lg61yxqu3">
            <span className="absolute left-[7px] top-2 hidden h-[calc(100%-2rem)] w-px bg-line md:block" aria-hidden="true" data-id="element-119" data-mp="1s3lkosnxtvmo" />
            {careerTimeline.map((role, index) => {
            const isOpen = openId === role.id;
            return <Reveal key={role.id} delay={index * 0.05} as="li" className="relative" data-id="element-120" data-mp="1i7sdloszmuca" data-mpi={index}>
                  <div className="md:pl-10" data-id="element-121" data-mp="2fv93enb1q0dv">
                    <span className={`absolute left-0 top-8 hidden h-[15px] w-[15px] rounded-full border-2 bg-white transition-colors duration-200 ease-premium md:block ${isOpen ? 'border-metal' : 'border-line'}`} aria-hidden="true" data-id="element-122" data-mp="2attiqwcs5vw0" />

                    <h3 data-id="element-123" data-mp="2ylquyok56pxn">
                      <button type="button" onClick={() => setOpenId(isOpen ? '' : role.id)} aria-expanded={isOpen} aria-controls={`role-${role.id}`} className="group flex w-full items-start justify-between gap-6 border-t border-line py-7 text-left transition-colors duration-150 ease-premium hover:border-navy/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy" data-id="element-124" data-mp="2tsxo9v5457ix">
                        <span className="min-w-0" data-id="element-125" data-mp="93updt9gncp8">
                          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-126" data-mp="97a5mzevers8">
                            <span className="font-serif text-[22px] font-semibold tracking-tight text-navy lg:text-2xl" data-id="element-127" data-mp="d4epn5pbywfl">
                              {role.company}
                            </span>
                            <span className="text-[12px] font-medium uppercase tracking-label text-metal" data-id="element-128" data-mp="47nucvilrlm2">
                              {role.period}
                            </span>
                          </span>
                          <span className="mt-2 block text-[15px] font-medium text-charcoal" data-id="element-129" data-mp="3j1wczyclut6g">
                            {role.role}
                          </span>
                          <span className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-charcoal-muted" data-id="element-130" data-mp="3e4mnkip7uhim">
                            <MapPinIcon className="h-3.5 w-3.5 text-charcoal-muted" aria-hidden="true" data-id="element-131" data-mp="3stivmfr9vuj8" />
                            {role.location}
                          </span>
                        </span>
                        <span className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center border transition-colors duration-150 ease-premium ${isOpen ? 'border-navy bg-navy text-white' : 'border-line text-navy group-hover:border-navy/40'}`} aria-hidden="true" data-id="element-132" data-mp="nx4btm9rpxq">
                          {isOpen ? <MinusIcon className="h-4 w-4" data-id="element-133" data-mp="13ghop56l3e2n" /> : <PlusIcon className="h-4 w-4" data-id="element-134" data-mp="18flzu1d2jlvm" />}
                        </span>
                      </button>
                    </h3>

                    <AnimatePresence initial={false} data-id="element-135" data-mp="1dfgnzx5glxcz">
                      {isOpen && <motion.div id={`role-${role.id}`} initial={reduceMotion ? undefined : {
                    height: 0,
                    opacity: 0
                  }} animate={reduceMotion ? undefined : {
                    height: 'auto',
                    opacity: 1
                  }} exit={reduceMotion ? undefined : {
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.25,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="overflow-hidden" data-id="element-136" data-mp="1hfqxvkwdh7x4">
                          <div className="bg-canvas p-7 lg:p-9" data-id="element-137" data-mp="1xfl0y60cy1r">
                            <p className="max-w-3xl text-[16px] leading-[1.7] text-charcoal" data-id="element-138" data-mp="5zsqdlguety0">
                              {role.summary}
                            </p>
                            <h4 className="mt-7 text-[11px] font-semibold uppercase tracking-label text-charcoal-muted" data-id="element-139" data-mp="3pndtcm8hrm5c">
                              Key achievements
                            </h4>
                            <ul className="mt-4 grid gap-4 md:grid-cols-3" data-id="element-140" data-mp="3go0l1ww3t3m7">
                              {role.achievements.map(achievement => <li key={achievement} className="border-t-2 border-metal/50 pt-4 text-[14px] leading-[1.65] text-charcoal-light" data-id="element-141" data-mp="3bq7fn62mkdmz">
                                  {achievement}
                                </li>)}
                            </ul>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </Reveal>;
          })}
          </ul>
        </div>
      </div>
    </section>;
}